import {
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { CodeHighlight as Prism } from "@mantine/code-highlight";
import { createStyles } from "@mantine/emotion";
import {
  IconBook,
  IconBracketsAngle,
  IconClipboardList,
  IconCode,
  IconExclamationCircle,
  IconInfoCircle,
  IconLock,
  IconMathFunction,
  IconPlaylistAdd,
  IconSettings,
  IconTemplate,
  IconTerminal2,
  IconVariable,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
}));

interface SectionHeaderProps {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly id: string;
}

const SectionHeader = ({ icon, title, id }: SectionHeaderProps) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

interface LuaBlockProps {
  readonly code: string;
}

const LuaBlock = ({ code }: LuaBlockProps) => (
  <Prism code={code.trim()} language="lua" />
);

// ---------------------------------------------------------------------------
// Static data tables sourced from backend source files
// ---------------------------------------------------------------------------

/**
 * Mirrors the backend `ScriptContextType` enum
 * (api-warrior/apps/monolith/src/rule-engine/lua/lua-sandbox.types.ts).
 * Keep in sync when a new script context is introduced.
 */
type ScriptContextType =
  | "CONDITION"
  | "ACTION"
  | "GENERATION"
  | "PRESET"
  | "CROSS_STEP"
  | "FORMULA";

/**
 * Mirrors the backend `ScriptReturnType` union
 * (api-warrior/apps/monolith/src/rule-engine/lua/validation/script-context.types.ts).
 */
type ScriptReturnType = "boolean" | "number" | "void" | "any";

/**
 * Every global surfaced by any Lua script context (per
 * api-warrior/apps/monolith/src/rule-engine/lua/validation/script-context.types.ts).
 */
type LuaGlobal =
  | "team"
  | "match"
  | "game"
  | "round"
  | "group"
  | "step"
  | "opponents"
  | "team_count"
  | "best_of"
  | "source_step";

type ScriptContextRow = {
  readonly context: ScriptContextType;
  readonly runsWhen: string;
  readonly returns: ScriptReturnType;
  readonly notes: string;
};

const SCRIPT_CONTEXT_ROWS: ReadonlyArray<ScriptContextRow> = [
  {
    context: "CONDITION",
    runsWhen: "Evaluating an advancement rule trigger",
    returns: "boolean",
    notes: "Read-only. Mutation functions are blocked.",
  },
  {
    context: "ACTION",
    runsWhen: "Executing an advancement rule whose condition matched",
    returns: "void",
    notes: "Emits effects via mutation functions (advance, eliminate, ...)",
  },
  {
    context: "GENERATION",
    runsWhen: "A GroupTemplate's generationScript materialises rounds/games",
    returns: "void",
    notes: "Emits structure effects (create_round, create_game, link).",
  },
  {
    context: "PRESET",
    runsWhen: "Applying a preset script to a StepRuleSet",
    returns: "void",
    notes: "Builds a full rule set (scoring, groups, rules, cross-step links).",
  },
  {
    context: "CROSS_STEP",
    runsWhen: "Computing a cross-step data transfer expression",
    returns: "any",
    notes: "Read-only. Used by cross-step rules between tournament steps.",
  },
  {
    context: "FORMULA",
    runsWhen: "Computing a scoring or tiebreaker formula",
    returns: "number",
    notes: "Read-only. Mutation functions are blocked.",
  },
];

type ContextGlobalRow = {
  readonly context: ScriptContextType;
  readonly globals: ReadonlyArray<LuaGlobal>;
  readonly functions: string;
};

const CONTEXT_GLOBAL_ROWS: ReadonlyArray<ContextGlobalRow> = [
  {
    context: "CONDITION",
    globals: ["team", "match", "game", "round", "group", "step", "opponents"],
    functions: "Pure helpers (rank, buchholz, sum, count, avg, min, max, coalesce, clamp, metadata, custom_field, by_id, ...)",
  },
  {
    context: "ACTION",
    globals: ["team", "match", "game", "round", "group", "step", "opponents"],
    functions:
      "Pure helpers + mutation functions (advance, eliminate, end_game, end_round, end_group, set_metadata, set_custom_field, set_status, set_winner, set_eliminated, register_team)",
  },
  {
    context: "GENERATION",
    globals: ["team_count", "best_of", "group"],
    functions: "Structure builders (create_round, create_game, link)",
  },
  {
    context: "PRESET",
    globals: ["team_count", "best_of"],
    functions:
      "Pure helpers + preset builders (set_scoring, add_tiebreaker, add_rule, add_cross_step_rule, create_group) + structure builders (create_round, create_game, link)",
  },
  {
    context: "CROSS_STEP",
    globals: ["team", "source_step"],
    functions: "Pure helpers only",
  },
  {
    context: "FORMULA",
    globals: ["team", "match", "game", "round", "group"],
    functions: "Pure helpers only",
  },
];

/**
 * Mirrors the backend `ScriptErrorCode` enum.
 * Keep in sync when a new diagnostic code is introduced.
 */
type ScriptErrorCode =
  | "SYNTAX"
  | "UNDEFINED_VARIABLE"
  | "INVALID_FUNCTION"
  | "TYPE_MISMATCH"
  | "RUNTIME"
  | "BLOCKED_FUNCTION"
  | "TIMEOUT"
  | "INSTRUCTION_LIMIT";

/** Mirrors the backend `ScriptWarningCode` enum. */
type ScriptWarningCode =
  | "UNUSED_VARIABLE"
  | "UNREACHABLE_CODE"
  | "DEPRECATED_FUNCTION";

type DiagnosticSeverity = "error" | "warning";

type ErrorCodeRow = {
  readonly code: ScriptErrorCode | ScriptWarningCode;
  readonly kind: DiagnosticSeverity;
  readonly description: string;
};

const ERROR_WARNING_ROWS: ReadonlyArray<ErrorCodeRow> = [
  {
    code: "SYNTAX",
    kind: "error",
    description: "The script failed to parse as valid Lua.",
  },
  {
    code: "UNDEFINED_VARIABLE",
    kind: "error",
    description:
      "The script referenced a global that is not available in this context.",
  },
  {
    code: "INVALID_FUNCTION",
    kind: "error",
    description:
      "The script called a function that is not available in this context.",
  },
  {
    code: "TYPE_MISMATCH",
    kind: "error",
    description:
      "The script's return type does not match the expected context return type (e.g. a CONDITION returning a number).",
  },
  {
    code: "RUNTIME",
    kind: "error",
    description:
      "The script raised a runtime error during execution (e.g. nil-indexing, bad argument).",
  },
  {
    code: "BLOCKED_FUNCTION",
    kind: "error",
    description:
      "The script called a disabled built-in (io, os, pcall, require, load, ...).",
  },
  {
    code: "TIMEOUT",
    kind: "error",
    description:
      "The script exceeded the execution time limit.",
  },
  {
    code: "INSTRUCTION_LIMIT",
    kind: "error",
    description:
      "The script exceeded the execution instruction budget.",
  },
  {
    code: "UNUSED_VARIABLE",
    kind: "warning",
    description: "A declared local variable is never read.",
  },
  {
    code: "UNREACHABLE_CODE",
    kind: "warning",
    description: "Code follows an unconditional return/error and can never execute.",
  },
  {
    code: "DEPRECATED_FUNCTION",
    kind: "warning",
    description: "The script uses a helper or global that has been deprecated.",
  },
];

type UnavailableGlobal =
  | "io"
  | "os"
  | "package"
  | "require"
  | "loadfile"
  | "dofile"
  | "load"
  | "loadstring"
  | "debug"
  | "coroutine"
  | "pcall"
  | "xpcall";

const UNAVAILABLE_GLOBALS: ReadonlyArray<UnavailableGlobal> = [
  "io",
  "os",
  "package",
  "require",
  "loadfile",
  "dofile",
  "load",
  "loadstring",
  "debug",
  "coroutine",
  "pcall",
  "xpcall",
];

const SINGLE_ELIM_EXAMPLE = `-- Single-elimination bracket preset.
-- Parameters (injected as globals):
--   team_count                            (number):  number of teams entered in the tournament
--   best_of                               (number):  best-of series length per game
--   promote_step_winners_to_tournament    (boolean): when true (default), the
--                                                    step's winners are promoted as
--                                                    winners of the tournament when
--                                                    the step ends.

-- 1. Normalise bracket size (round up to next power of 2) using the
--    built-in helper \`next_pow2_and_log2\`.
local bracket_size, rounds = next_pow2_and_log2(math.max(team_count, 2))

-- 2. Scoring + tiebreakers.
set_scoring({
  aggregation    = "SUM",
  win_points     = 1,
  loss_points    = 0,
  draw_points    = 0,
  forfeit_points = 0,
})
add_tiebreaker({ type = "HEAD_TO_HEAD", order = 1, sort = "DESC" })

-- 3. Main bracket group, rounds, and games. Winner of (r, g) feeds into
--    (r + 1, ceil(g / 2)).
local bracket = create_group({ name = "Bracket", team_slots = bracket_size })

local prev_round_games = {}
for r = 1, rounds do
  local round = create_round(bracket, { name = string.format("Round %d", r), order = r })
  local games_in_round = bracket_size // (2 ^ r)
  local current_games = {}
  for g = 1, games_in_round do
    local gm = create_game(round, { order = g, team_slots = 2, best_of = best_of })
    table.insert(current_games, gm)
    if r > 1 then
      link(prev_round_games[g * 2 - 1], gm, "WIN")
      link(prev_round_games[g * 2],     gm, "WIN")
    end
  end
  prev_round_games = current_games
end

-- 4. Rules — best-of resolution, advancement, elimination, promotion.
add_best_of_series_resolution_rule(best_of, 1)
add_winner_advances_rule(2)
add_loser_eliminated_rule(3)
add_promote_step_winners_rule(promote_step_winners_to_tournament)
`;

const SCORING_FORMULA_EXAMPLE = `-- FORMULA context — returns a number used as the team's step score.
-- Available globals: team, match, game, round, group.
-- Available functions: pure helpers only (sum, count, buchholz, ...).
-- Example: weighted score = 3 * wins + 1 * draws - 0.5 * forfeits.

local wins     = count(team.matches, function(m) return m.result == "WIN"  end)
local draws    = count(team.matches, function(m) return m.result == "DRAW" end)
local forfeits = count(team.matches, function(m) return m.forfeit == true  end)

return 3 * wins + 1 * draws - 0.5 * forfeits
`;

const ADVANCEMENT_CONDITION_EXAMPLE = `-- CONDITION context — returns a boolean. The rule fires when this is true.
-- Available globals: team, match, game, round, group, step, opponents.
-- \`opponents\` is always a 1-indexed Lua sequence (array). Use \`#opponents\`
-- for the count and \`ipairs(opponents)\` to iterate. The shape scales with
-- the event: 0 entries (solo), 1 (head-to-head), N−1 (battle royale / FFA).
-- Available functions: pure helpers only (no mutations).
-- Example: advance when a team has won the best-of-N majority AND has
-- strictly outscored every opponent in the current event.

local needed = math.ceil(game.best_of / 2)
if team.matches_won < needed then
  return false
end

for _, opp in ipairs(opponents) do
  if (opp.score or 0) >= (team.score or 0) then
    return false
  end
end

return true
`;

const ACTION_EXAMPLE = `-- ACTION context — runs when its CONDITION returned true.
-- Available globals: same as CONDITION.
-- Available functions: pure helpers + mutation functions.
-- Example: advance the current team along the WIN link.

if game.win_link_target ~= nil then
  advance(team, by_id(game.win_link_target))
else
  set_winner(team, step)
end
`;

const RANK_BASED_STEP_WINNER_EXAMPLE = `-- Top-1 winner by rank (default when winners_count is omitted).
-- Placement: call before add_promote_step_winners_rule(...) because the
-- promote rule reads the step.winners that this rule populates.
add_rank_based_step_winner_rule(1)

-- Top-4 (e.g. for a round-robin feeding a 4-team playoff bracket).
add_rank_based_step_winner_rule(4)

-- Top-1, but co-champions on a full points+tiebreaker tie
-- (e.g. cumulative-points-total's shared_victory parameter).
add_rank_based_step_winner_rule(1, shared_victory)
`;

const BUILDER_ID_WRONG_EXAMPLE = `-- WRONG: bakes a synthetic preset-time id into an action script.
-- The scanner rejects this at preset apply with RULE_ACTION_INVALID_ID.
local gm = create_game(rnd, { order = 1, team_slots = 4, best_of = 1 })
add_rule({
  trigger = "STEP_SEEDING",
  actions = {{
    type   = "EXECUTE_LUA",
    script = 'advance(team, by_id("' .. gm.id .. '"))',
  }},
})
`;

const BUILDER_ID_RIGHT_EXAMPLE = `-- RIGHT: walk the hydrated runtime tree and reference real entities.
-- step.groups / group.rounds / round.games carry real runtime entities
-- at action-time, so no by_id lookup is needed.
add_rule({
  trigger = "STEP_SEEDING",
  actions = {{
    type   = "EXECUTE_LUA",
    script = [[
      for _, g in ipairs(step.groups) do
        if g.order == 0 then
          for _, rnd in ipairs(g.rounds) do
            advance(team, rnd.games[1])
          end
        end
      end
    ]],
  }},
})
`;

const PARAMETER_EXAMPLE = `-- A preset declares its parameters through the PresetScript GraphQL type.
-- Each parameter has: name, type, required, defaultValue, description.
-- Parameter values are injected as Lua globals before the script runs, so a
-- preset with parameters named team_count, best_of, and
-- promote_step_winners_to_tournament reads them directly:

if team_count < 2 then
  error("At least 2 teams are required")
end

set_scoring({
  aggregation = "SUM",
  win_points  = 1,
  loss_points = 0,
})

if promote_step_winners_to_tournament then
  add_promote_step_winners_rule(true)
end
`;

export const TournamentLuaReference = () => {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Lua API Reference"
        subtitle="Complete reference for Lua scripts used by the tournament rule engine"
        showBreadcrumbs={false}
      />

      {/* Table of contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#contexts">Script Contexts</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#globals">Available Globals by Context</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#scopes">Value Scopes</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#preset-builder">Preset Builder API</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#preset-builder-ids">
                Preset-time ids vs runtime ids
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#prelude-helpers">Prelude Rule Factories</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#metadata-custom-fields">
                Metadata &amp; Custom Fields
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#preset-reapply">Preset Re-Apply Semantics</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#generation">Generation API</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#parameters">Parameters</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#sandbox">Script Limits</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#errors">Error &amp; Warning Codes</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#example-single-elim">
                Example: Single-Elim Preset
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#example-scoring">Example: Scoring Formula</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#example-advancement">
                Example: Advancement Condition
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#best-practices">Best Practices</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBook size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The tournament rule engine runs user-authored Lua scripts in a
            sandboxed runtime. Lua is used everywhere custom, format-specific
            logic is needed:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Scoring formulas</strong> — compute a team's score for a
              step.
            </List.Item>
            <List.Item>
              <strong>Advancement conditions</strong> — decide when an
              advancement rule should fire.
            </List.Item>
            <List.Item>
              <strong>Advancement actions</strong> — emit the effects that
              happen when a rule fires (advance, eliminate, ...).
            </List.Item>
            <List.Item>
              <strong>Cross-step expressions</strong> — transfer data between
              tournament steps (seeding, qualification, score injection).
            </List.Item>
            <List.Item>
              <strong>Structure generation</strong> — dynamically build rounds
              and games for a group.
            </List.Item>
            <List.Item>
              <strong>Presets</strong> — build a full rule set (scoring,
              tiebreakers, structure, rules) in one script.
            </List.Item>
          </List>
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Every script runs in a fresh, isolated environment. No state leaks
            between executions. Side effects are applied transactionally once
            the script finishes successfully.
          </Alert>

          <Alert
            icon={<IconTemplate />}
            color="blue"
            variant="light"
            mt="md"
            title="Need a starting point?"
          >
            <Text size="sm">
              The platform&apos;s{" "}
              <Anchor href="/tournaments/docs#builtin-templates">
                built-in templates
              </Anchor>{" "}
              ship with full Lua sources covering every common tournament
              format. Preview a template&apos;s source via the{" "}
              <Code>builtinPreset(id)</Code> query, or copy it into an editable
              preset script with the <Code>duplicateBuiltinPreset</Code>{" "}
              mutation. The console&apos;s &ldquo;Start from a template&rdquo;
              picker on the new-preset form does this in one click &mdash; load
              a template, edit, save.
            </Text>
          </Alert>
        </div>

        {/* ===================== SCRIPT CONTEXTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTerminal2 size={18} />}
            title="Script Contexts"
            id="contexts"
          />
          <Text mb="md">
            A script's <Code>ScriptContextType</Code> determines which globals
            and functions are available and what return shape it must produce.
          </Text>
          <Table striped highlightOnHover mb="md">
            <thead>
              <tr>
                <th>Context</th>
                <th>Runs when</th>
                <th>Returns</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {SCRIPT_CONTEXT_ROWS.map((row) => (
                <tr key={row.context}>
                  <td>
                    <Code>{row.context}</Code>
                  </td>
                  <td>{row.runsWhen}</td>
                  <td>
                    <Code>{row.returns}</Code>
                  </td>
                  <td>{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* ===================== GLOBALS BY CONTEXT ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconVariable size={18} />}
            title="Available Globals by Context"
            id="globals"
          />
          <Text mb="md">
            Each context exposes a fixed set of globals and helper functions.
            Referencing an undeclared global triggers{" "}
            <Code>UNDEFINED_VARIABLE</Code>; calling an unavailable function
            triggers <Code>INVALID_FUNCTION</Code>.
          </Text>
          <Table striped highlightOnHover mb="md">
            <thead>
              <tr>
                <th>Context</th>
                <th>Globals</th>
                <th>Functions</th>
              </tr>
            </thead>
            <tbody>
              {CONTEXT_GLOBAL_ROWS.map((row) => (
                <tr key={row.context}>
                  <td>
                    <Code>{row.context}</Code>
                  </td>
                  <td>
                    {row.globals.map((g, idx) => (
                      <span key={g}>
                        <Code>{g}</Code>
                        {idx < row.globals.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </td>
                  <td>
                    <Text size="sm">{row.functions}</Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Pure helpers (available in every context except GENERATION)
          </Title>
          <Text mb="sm" size="sm">
            Ranking / scoring / aggregation / metadata helpers.
          </Text>
          <List size="sm" spacing={4} mb="md">
            <List.Item>
              <strong>Ranking / scoring:</strong> <Code>rank</Code>,{" "}
              <Code>buchholz</Code>, <Code>sonnborn_berger</Code>,{" "}
              <Code>median_buchholz</Code>, <Code>cumulative</Code>,{" "}
              <Code>head_to_head</Code>, <Code>step_rank</Code>,{" "}
              <Code>step_score</Code>
            </List.Item>
            <List.Item>
              <strong>Metadata / custom fields:</strong> <Code>metadata</Code>,{" "}
              <Code>custom_field</Code>, <Code>by_id</Code> — see{" "}
              <Anchor href="#metadata-custom-fields">
                Metadata &amp; Custom Fields
              </Anchor>{" "}
              for the full read/write contract (paired with{" "}
              <Code>set_metadata</Code> / <Code>set_custom_field</Code>).
            </List.Item>
            <List.Item>
              <strong>Aggregation:</strong> <Code>sum</Code>, <Code>count</Code>
              , <Code>avg</Code>, <Code>min</Code>, <Code>max</Code>
            </List.Item>
            <List.Item>
              <strong>Utility:</strong> <Code>coalesce</Code>,{" "}
              <Code>clamp</Code>
            </List.Item>
            <List.Item>
              <strong>Team status filters:</strong> <Code>winners</Code>,{" "}
              <Code>qualified</Code>, <Code>eliminated</Code>,{" "}
              <Code>status</Code>
            </List.Item>
          </List>
        </div>

        {/* ===================== VALUE SCOPES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBracketsAngle size={18} />}
            title="Value Scopes"
            id="scopes"
          />
          <Text mb="md">
            The engine exposes scoped value objects as Lua globals inside
            CONDITION / ACTION / FORMULA / CROSS_STEP scripts. The exact fields
            a scope carries depend on the event being evaluated; the list below
            reflects what presets and built-in helpers routinely read. Refer to
            the live simulation output when authoring a new field reference.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>team</Code>
              </Text>
              <Text size="sm">
                The current team being evaluated. Commonly read:{" "}
                <Code>team.id</Code>, <Code>team.status</Code>,{" "}
                <Code>team.rank</Code>, <Code>team.matches</Code>,{" "}
                <Code>team.matches_won</Code>, <Code>team.stepRanks</Code>,{" "}
                <Code>team.stepScores</Code>. Passed as the first argument to
                mutation functions such as <Code>advance</Code> and{" "}
                <Code>set_winner</Code>.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>opponents</Code>
              </Text>
              <Text size="sm">
                Sequence of every other team participating in the current
                event, excluding <Code>team</Code>. Indexed 1..N — use{" "}
                <Code>#opponents</Code> for the count and{" "}
                <Code>ipairs(opponents)</Code> to iterate. Each entry is an{" "}
                <Code>OpponentContext</Code> projection with{" "}
                <Code>opponents[i].id</Code> (string, always present),{" "}
                <Code>opponents[i].score</Code> (number, optional), and{" "}
                <Code>opponents[i].result</Code> (<Code>"WIN"</Code>/
                <Code>"LOSS"</Code>/<Code>"DRAW"</Code>/<Code>"FORFEIT"</Code>,
                optional). Reach for <Code>by_id(opponents[i].id)</Code> when
                richer fields (rank, metadata, ...) are needed. Empty for solo
                events, 1 entry for head-to-head, N−1 for battle royale or
                free-for-all.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>game</Code>
              </Text>
              <Text size="sm">
                Commonly read: <Code>game.id</Code>, <Code>game.result</Code>{" "}
                (<Code>"WIN"</Code>/<Code>"LOSS"</Code>),{" "}
                <Code>game.best_of</Code>, <Code>game.win_link_target</Code>,{" "}
                <Code>game.loss_link_target</Code>. The link-target fields hold
                the target game's UUID — feed them to <Code>by_id(...)</Code>{" "}
                to turn them into a reference.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>match</Code>
              </Text>
              <Text size="sm">
                An individual confrontation inside a best-of-N game. Commonly
                read: <Code>match.result</Code> (
                <Code>"WIN"</Code>/<Code>"LOSS"</Code>/<Code>"DRAW"</Code>),{" "}
                <Code>match.forfeit</Code>.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>round</Code>
              </Text>
              <Text size="sm">
                Hydrated fields: <Code>round.id</Code>,{" "}
                <Code>round.type</Code>, <Code>round.order</Code>,{" "}
                <Code>round.games</Code> (sequence of{" "}
                <Code>{`{ id, order, teams[].id }`}</Code> entries). Passed to{" "}
                <Code>end_round(round)</Code> to close a round early.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>group</Code>
              </Text>
              <Text size="sm">
                Hydrated fields: <Code>group.id</Code>,{" "}
                <Code>group.type</Code>, <Code>group.order</Code>,{" "}
                <Code>group.teams</Code> (all teams in the group), and{" "}
                <Code>group.rounds</Code> — each entry carries{" "}
                <Code>id</Code>, <Code>order</Code>, and{" "}
                <Code>games[]</Code> with <Code>id</Code>,{" "}
                <Code>order</Code>, and <Code>teams[].id</Code>. Passed to{" "}
                <Code>end_group(group)</Code> and{" "}
                <Code>create_round(group, ...)</Code>.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>step</Code>
              </Text>
              <Text size="sm">
                Hydrated fields: <Code>step.id</Code>,{" "}
                <Code>step.type</Code>, <Code>step.teams</Code> (
                <strong>pre-ranked</strong> sequence of{" "}
                <Code>{`{ id, status, rank }`}</Code> entries), and{" "}
                <Code>step.winners</Code> (subset of <Code>step.teams</Code>{" "}
                whose step-scope status is <Code>WINNER</Code>, populated by
                rules such as <Code>add_rank_based_step_winner_rule</Code>).
                Target scope for <Code>set_winner(team, step)</Code> /{" "}
                <Code>set_eliminated(team, step)</Code>.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>source_step</Code>
              </Text>
              <Text size="sm">
                Only exposed in <Code>CROSS_STEP</Code> context. Represents the
                step that produced the data being transferred.
              </Text>
            </Card>
          </SimpleGrid>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Field enumeration: the platform does not publish an exhaustive
            per-scope schema. When authoring a new field reference, test it
            against the live simulation output or consult a built-in preset
            for idiomatic accessor patterns.
          </Alert>

          <Alert
            icon={<IconInfoCircle />}
            color="cyan"
            variant="light"
            mt="md"
            title="Rich scope contexts at rule-execution time"
          >
            <Text size="sm">
              Inside <Code>STEP_ENDED</Code>, <Code>GROUP_ENDED</Code>,{" "}
              <Code>ROUND_ENDED</Code>, and <Code>GAME_ENDED</Code> action
              scripts, the engine hydrates the triggering scope with the
              fields listed in the cards above (e.g. <Code>step.teams</Code>{" "}
              pre-ranked, <Code>step.winners</Code>,{" "}
              <Code>group.rounds[].games[].teams[].id</Code>). Previously only{" "}
              <Code>{`{ id, type }`}</Code> was exposed, so preset actions had
              to re-fetch data via helpers; the richer context lets a rule
              read the full structure directly off the global.
            </Text>
          </Alert>
        </div>

        {/* ===================== PRESET BUILDER API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTemplate size={18} />}
            title="Preset Builder API"
            id="preset-builder"
          />
          <Text mb="md">
            A <Code>PRESET</Code> script builds a complete rule set by calling
            builder functions. Each call emits a typed effect that is later
            persisted transactionally when the preset is applied.
          </Text>

          <Title order={4} mb="xs">
            Top-level rule-set builders
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>set_scoring(config)</Code> — Configure the step's scoring
              rule set. Supported <Code>config</Code> keys:{" "}
              <Code>aggregation</Code> (<Code>"SUM"</Code>,{" "}
              <Code>"AVERAGE"</Code>, <Code>"POINTS"</Code>,{" "}
              <Code>"FORMULA"</Code>, <Code>"CUSTOM_LUA"</Code>),{" "}
              <Code>formula</Code> (Lua string),{" "}
              <Code>win_points</Code>, <Code>loss_points</Code>,{" "}
              <Code>draw_points</Code>, <Code>forfeit_points</Code>. May be
              called once per preset.
            </List.Item>
            <List.Item>
              <Code>add_tiebreaker(config)</Code> — Append a tiebreaker.{" "}
              <Code>type</Code> accepts <Code>HEAD_TO_HEAD</Code>,{" "}
              <Code>SCORE_DIFFERENCE</Code>, <Code>TOTAL_SCORE</Code>,{" "}
              <Code>WINS</Code>, <Code>BUCHHOLZ</Code>,{" "}
              <Code>SONNBORN_BERGER</Code>, <Code>MEDIAN_BUCHHOLZ</Code>,{" "}
              <Code>CUMULATIVE</Code>, <Code>CUSTOM_FORMULA</Code>.{" "}
              <Code>order</Code>, <Code>sort</Code> (<Code>"ASC"</Code>/
              <Code>"DESC"</Code>), <Code>formula</Code> (for{" "}
              <Code>CUSTOM_FORMULA</Code>).
            </List.Item>
            <List.Item>
              <Code>add_rule(config)</Code> — Declare an advancement rule.{" "}
              <Code>name</Code>, <Code>order</Code>, <Code>trigger</Code> (
              <Code>MATCH_ENDED</Code>, <Code>GAME_ENDED</Code>,{" "}
              <Code>ROUND_ENDED</Code>, <Code>GROUP_ENDED</Code>,{" "}
              <Code>STEP_ENDED</Code>, <Code>MANUAL</Code>),{" "}
              <Code>condition</Code> (Lua string), <Code>actions</Code> (array
              of <Code>{`{ type, script }`}</Code> entries with type ∈{" "}
              <Code>ADVANCE</Code>, <Code>ELIMINATE</Code>, <Code>END</Code>,{" "}
              <Code>EXECUTE_LUA</Code>), <Code>auto_execute</Code>,{" "}
              <Code>description</Code>, <Code>purpose</Code>.
            </List.Item>
            <List.Item>
              <Code>add_cross_step_rule(config)</Code> — Pull data from another
              step. <Code>source</Code> (<Code>"PREVIOUS"</Code>,{" "}
              <Code>"NEXT"</Code>, <Code>"CURRENT"</Code>),{" "}
              <Code>sourceStepId</Code> (UUID; optional),{" "}
              <Code>data_source</Code> (<Code>TEAMS</Code>,{" "}
              <Code>WINNERS</Code>, <Code>QUALIFIED</Code>,{" "}
              <Code>ELIMINATED</Code>, <Code>WITHDRAWN</Code>,{" "}
              <Code>RANKINGS</Code>, <Code>SCORES</Code>), <Code>usage</Code>{" "}
              (free-form object narrowed at the boundary).
            </List.Item>
            <List.Item>
              <Code>create_group(config)</Code> — Create a structure-template
              group. <Code>name</Code>, <Code>description</Code>,{" "}
              <Code>team_slots</Code>, <Code>best_of</Code>,{" "}
              <Code>team_slots_per_game</Code>, <Code>round_count</Code>,{" "}
              <Code>generation_script</Code>. Returns a <Code>{`{ id }`}</Code>{" "}
              handle used by <Code>create_round</Code>.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Empty / missing-value coercion
          </Title>
          <Text mb="md" size="sm">
            Helpers that read missing or empty data return Lua{" "}
            <Code>nil</Code> (not an opaque JS-null sentinel), so the idiomatic{" "}
            <Code>metadata(t, &quot;foo&quot;) or default_value</Code> pattern
            works as expected. The same coercion applies to{" "}
            <Code>min</Code>, <Code>max</Code>, and <Code>coalesce</Code> when
            every argument / collection entry is empty or nil — they return{" "}
            <Code>nil</Code>, not a value that silently turns truthy under{" "}
            <Code>or</Code> and then blows up as soon as you try to use it as a
            number or string. The full read/write contract for{" "}
            <Code>metadata</Code> / <Code>custom_field</Code> lives in the{" "}
            <Anchor href="#metadata-custom-fields">
              Metadata &amp; Custom Fields
            </Anchor>{" "}
            section.
          </Text>

          <Title order={4} mb="xs">
            Structure builders (also available in GENERATION context)
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>create_round(group, config)</Code> — Add a round inside a
              group. <Code>name</Code>, <Code>order</Code>. Returns a{" "}
              <Code>{`{ id, name }`}</Code> handle.
            </List.Item>
            <List.Item>
              <Code>create_game(round, config)</Code> — Add a game inside a
              round. <Code>order</Code>, <Code>team_slots</Code> (1-1024),{" "}
              <Code>best_of</Code> (1-99). Returns a <Code>{`{ id }`}</Code>{" "}
              handle.
            </List.Item>
            <List.Item>
              <Code>link(sourceGame, targetGame, outcome)</Code> — Route the
              winner/loser of <Code>sourceGame</Code> into{" "}
              <Code>targetGame</Code>. <Code>outcome</Code> must be{" "}
              <Code>"WIN"</Code> or <Code>"LOSS"</Code> — anything else throws
              at runtime.
            </List.Item>
          </List>

          <Title order={4} mt="md" mb="xs" id="preset-builder-ids">
            Preset-time ids vs runtime ids
          </Title>
          <Text mb="md">
            <Code>create_group</Code>, <Code>create_round</Code>, and{" "}
            <Code>create_game</Code> each return a small handle table whose{" "}
            <Code>id</Code> field is a <strong>synthetic placeholder</strong>{" "}
            minted by the preset builder (e.g. <Code>"group-1"</Code>,{" "}
            <Code>"round-3"</Code>, <Code>"game-7"</Code>). These synthetic
            ids exist only inside the preset script that just emitted them,
            and their only legitimate use is feeding{" "}
            <Code>link(sourceGame, targetGame, outcome)</Code> so the preset
            can wire its bracket connections while it is still being built.
          </Text>
          <Text mb="md">
            They are <strong>not</strong> real entity ids. When the preset is
            applied, the engine persists the structure and assigns a fresh
            runtime UUID to every group, round, and game — the synthetic
            strings from the builder are discarded and no row in the database
            will ever match them.
          </Text>
          <Alert
            icon={<IconExclamationCircle />}
            color="red"
            variant="light"
            mb="md"
            title="Never reference builder ids from rule scripts"
          >
            <Text size="sm">
              Rule <Code>ACTION</Code> scripts and <Code>CONDITION</Code>{" "}
              scripts must never reference builder ids — neither through{" "}
              <Code>by_id("game-2")</Code> nor through identity comparisons
              such as <Code>group.id == "group-1"</Code>. At match-play time
              those strings do not exist. The preset-apply scanner rejects
              such scripts up-front with <Code>RULE_ACTION_INVALID_ID</Code>;
              if a script slips through, the runtime surfaces the same code
              (typically via a Prisma UUID parse error on{" "}
              <Code>by_id</Code>).
            </Text>
          </Alert>
          <Text mb="xs">
            To reference structural entities from an action or condition
            script, walk the hydrated scope globals. Every entry in{" "}
            <Code>step.groups</Code>, <Code>group.rounds</Code>, and{" "}
            <Code>round.games</Code> is a real runtime entity that can be
            passed directly to mutation helpers such as{" "}
            <Code>advance(team, game)</Code>, <Code>end_game(game)</Code>, or{" "}
            <Code>end_round(round)</Code> — no <Code>by_id</Code> lookup
            needed.
          </Text>
          <Text mb="md">
            For identity checks inside conditions, compare against the
            0-based <Code>order</Code> field (<Code>group.order</Code>,{" "}
            <Code>round.order</Code>, <Code>game.order</Code>) — it is stable,
            declared by the preset&apos;s structure template, and meaningful
            across re-applies. Never compare <Code>group.id</Code> or{" "}
            <Code>round.id</Code> against a hardcoded string. Note that Lua
            tables are 1-indexed while the <Code>order</Code> fields are
            0-based, so the first group is <Code>step.groups[1]</Code> and its{" "}
            <Code>.order</Code> is <Code>0</Code>.
          </Text>
          <Text mb="xs">
            <strong>Wrong</strong> — rejected at preset apply:
          </Text>
          <LuaBlock code={BUILDER_ID_WRONG_EXAMPLE} />
          <Text mt="md" mb="xs">
            <strong>Right</strong> — walk the hydrated tree at action time:
          </Text>
          <LuaBlock code={BUILDER_ID_RIGHT_EXAMPLE} />

        </div>

        {/* ===================== PRELUDE RULE FACTORIES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPlaylistAdd size={18} />}
            title="Prelude Rule Factories"
            id="prelude-helpers"
          />
          <Text mb="md">
            The <Code>PRESET</Code> sandbox exposes a library of{" "}
            <strong>prelude rule factories</strong> &mdash; helpers that emit
            one or more <Code>add_rule(...)</Code> entries so presets do not
            have to hand-author the same trigger / condition / action
            boilerplate over and over. Each factory persists as a normal
            advancement rule after the preset is applied.
          </Text>

          <Title order={4} mb="xs">
            <Code>
              add_rank_based_step_winner_rule(winners_count, shared_victory)
            </Code>
          </Title>
          <Text mb="xs">
            Emits a <Code>STEP_ENDED</Code> rule that reads the pre-ranked{" "}
            <Code>step.teams</Code> sequence and marks the top{" "}
            <Code>winners_count</Code> as <Code>WINNER</Code> at step scope;
            every other team is marked <Code>ELIMINATED</Code> at step scope.{" "}
            <Code>winners_count</Code> defaults to <Code>1</Code>.
          </Text>
          <Text mb="xs" size="sm">
            Used by the round-robin, Swiss, cumulative-rounds-series,
            cumulative-points-total, battle-royale, points-race,
            Buchholz-groups, and league-home-away built-in presets to declare
            their step winner(s) in one call.
          </Text>
          <LuaBlock code={RANK_BASED_STEP_WINNER_EXAMPLE} />
          <Text mt="md" mb="xs" size="sm">
            <strong>
              <Code>shared_victory</Code> (optional, defaults to falsy)
            </strong>{" "}
            replaces the fixed top-<Code>winners_count</Code> cut with a
            tie-aware dynamic cut: every team sharing the top{" "}
            <Code>shared_rank</Code> (materialised on each{" "}
            <Code>step.teams</Code> entry — teams still fully tied after
            every configured tiebreaker share the same value, while{" "}
            <Code>rank</Code> stays strictly sequential) is declared a{" "}
            <Code>WINNER</Code>, the rest <Code>ELIMINATED</Code>. On a full
            points+tiebreaker tie at the top, this produces co-champions
            instead of a single deterministic pick. When{" "}
            <Code>shared_victory</Code> is <Code>nil</Code>/<Code>false</Code>
            , the emitted script is byte-identical to the historical
            fixed-<Code>n</Code> form. Used by{" "}
            <Code>cumulative-points-total</Code>&apos;s{" "}
            <Code>shared_victory</Code> parameter.
          </Text>
          <Alert
            icon={<IconInfoCircle />}
            color="cyan"
            variant="light"
            mt="md"
          >
            <Text size="sm">
              <strong>Placement matters.</strong> Call{" "}
              <Code>add_rank_based_step_winner_rule(...)</Code>{" "}
              <em>before</em>{" "}
              <Code>add_promote_step_winners_rule(...)</Code>: the promote
              rule reads <Code>step.winners</Code>, which is populated by the
              rank-based rule. Reversing the order produces an empty winner
              set on the tournament.
            </Text>
          </Alert>

          <Title order={4} mt="md" mb="xs">
            Companion factories
          </Title>
          <Text mb="xs" size="sm">
            The following helpers are used by the built-in templates and share
            the same &ldquo;emit one canonical rule&rdquo; shape:
          </Text>
          <List size="sm" spacing={4} mb="md">
            <List.Item>
              <Code>add_best_of_series_resolution_rule(best_of, order)</Code>{" "}
              &mdash; emits a <Code>MATCH_ENDED</Code> rule that closes a
              best-of-N game once a team reaches{" "}
              <Code>ceil(best_of / 2)</Code> match wins.
            </List.Item>
            <List.Item>
              <Code>add_winner_advances_rule(order)</Code> &mdash; emits a{" "}
              <Code>GAME_ENDED</Code> rule that advances the winning team
              along the <Code>WIN</Code> link between games.
            </List.Item>
            <List.Item>
              <Code>add_loser_eliminated_rule(order)</Code> &mdash; emits a{" "}
              <Code>GAME_ENDED</Code> rule that marks the losing team as
              eliminated (or advances along a <Code>LOSS</Code> link when one
              is wired).
            </List.Item>
            <List.Item>
              <Code>
                add_promote_step_winners_rule(promote_to_tournament)
              </Code>{" "}
              &mdash; emits a <Code>STEP_ENDED</Code> rule that promotes every
              team in <Code>step.winners</Code> to the tournament-level
              winners list when <Code>promote_to_tournament</Code> is true.
            </List.Item>
          </List>
        </div>

        {/* ===================== SEEDING & WITHDRAWAL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPlaylistAdd size={18} />}
            title="Seeding & Withdrawal API"
            id="seeding-withdrawal"
          />
          <Text mb="md">
            The rule engine exposes dedicated <Code>SEEDING</Code> and{" "}
            <Code>WITHDRAWAL_RESPONSE</Code> rule purposes. A preset declares
            exactly one rule of each purpose, and the prelude ships helpers
            that compose the canonical bodies so presets rarely need to
            hand-author either.
          </Text>

          <Title order={4} mb="xs">
            Triggers
          </Title>
          <Table striped highlightOnHover mb="md">
            <thead>
              <tr>
                <th>Trigger</th>
                <th>Fires when</th>
                <th>Globals available in ACTION</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>STEP_SEEDING</Code>
                </td>
                <td>
                  <Code>seedTournamentStep</Code> runs, after CrossStep rules
                  have injected scores. Seeding rules fire exactly once per
                  step per seed attempt.
                </td>
                <td>
                  <Code>step</Code> (with <Code>seeded_teams</Code>,{" "}
                  <Code>manual_pins</Code>, <Code>groups</Code>)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>ROUND_SEEDING</Code>
                </td>
                <td>
                  At the start of every round of a multi-round scheduled
                  format (Swiss, round-robin). Use this to compute per-round
                  pairings instead of pre-seeding every round in{" "}
                  <Code>STEP_SEEDING</Code>.
                </td>
                <td>
                  <Code>step</Code>, <Code>round</Code>,{" "}
                  <Code>group</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>TEAM_WITHDREW</Code>
                </td>
                <td>
                  <Code>withdrawTeamFromStep</Code> runs. Fires once per
                  withdrawal; idempotent on already-withdrawn teams.
                </td>
                <td>
                  <Code>step</Code>, <Code>team</Code>
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Purposes
          </Title>
          <Table striped highlightOnHover mb="md">
            <thead>
              <tr>
                <th>Purpose</th>
                <th>Scope</th>
                <th>Emitted effects</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>SEEDING</Code>
                </td>
                <td>Placement of teams into entry-round game slots.</td>
                <td>
                  <Code>advance(team, game)</Code> (one per team per slot),
                  optional <Code>set_metadata</Code> for seed labels.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>WITHDRAWAL_RESPONSE</Code>
                </td>
                <td>Cascade of a withdrawal through existing bracket state.</td>
                <td>
                  <Code>set_winner</Code>, <Code>end_game</Code>,{" "}
                  <Code>set_match_result</Code>, <Code>reseed_step</Code>,{" "}
                  <Code>set_metadata</Code>.
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Step bindings for seeding / withdrawal
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>step.seeded_teams</Code> — ordered sequence of teams to
              place. Corresponds directly to the caller&apos;s{" "}
              <Code>orderedTeamIds</Code> in <Code>seedTournamentStep</Code>.
            </List.Item>
            <List.Item>
              <Code>step.manual_pins</Code> — array of{" "}
              <Code>{`{ team_id, group_id?, game_id?, slot_index? }`}</Code>{" "}
              pins submitted by the caller. Empty if no pins were provided.
              The prelude&apos;s <Code>apply_group_pins</Code> /{" "}
              <Code>apply_slot_pins</Code> helpers consume this.
            </List.Item>
            <List.Item>
              <Code>step.groups</Code> — groups of the generated structure,
              each with <Code>rounds[].games[].matches[]</Code>.
            </List.Item>
            <List.Item>
              <Code>step.status</Code> — useful in{" "}
              <Code>WITHDRAWAL_RESPONSE</Code> rules to branch between
              pre-start (full re-seed) and post-start (cascade) behaviour.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Placement helpers
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>seed_bracket_r1(group, teams, pinned)</Code> — classic
              elimination bracket: pair seed <em>i</em> with seed{" "}
              <em>n+1-i</em> into round-1 games. Used by{" "}
              <Code>single-elim</Code>, <Code>double-elim</Code>,{" "}
              <Code>triple-elim</Code>.
            </List.Item>
            <List.Item>
              <Code>seed_all_rounds_full_roster(group, teams, pinned)</Code>{" "}
              — every team enters every round&apos;s first game. Used for
              battle-royale, points-race, cumulative-rounds-series,
              cumulative-points-total.
            </List.Item>
            <List.Item>
              <Code>seed_round_robin_rounds(group, teams, pinned)</Code> —
              circle-method pairings for all rounds. Used by{" "}
              <Code>round-robin</Code> and <Code>league-home-away</Code>.
            </List.Item>
            <List.Item>
              <Code>seed_chain(group, teams, pinned)</Code> — gauntlet /
              step-ladder / king-of-the-hill: seed n and n-1 enter round 1;
              subsequent seeds enter their own rounds.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Repartition helpers
          </Title>
          <Text size="sm" mb="xs">
            Called by <Code>add_seeding_rule_using(repartition, placement)</Code>
            before the placement helper runs. Decides which teams go into
            which group. All variants respect <Code>pin_by_team</Code>{" "}
            (derived from <Code>apply_group_pins(step)</Code>) — pinned teams
            bypass the algorithm.
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>repartition.entry_only</Code> — all non-pinned teams go
              into the first group. Pinned teams land in their pinned group.
            </List.Item>
            <List.Item>
              <Code>repartition.balanced</Code> — round-robin distribution of
              unpinned teams across groups. Most common for group-stage
              formats.
            </List.Item>
            <List.Item>
              <Code>repartition.snake</Code> — snake-draft distribution (seed
              1 → group 1, seed 2 → group 2, seed 3 → group 2, seed 4 → group
              1, …). Balances average seed across groups.
            </List.Item>
            <List.Item>
              <Code>repartition.explicit</Code> — every non-pinned team must
              have an explicit group assignment in <Code>manual_pins</Code>;
              otherwise <Code>error()</Code> is thrown.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Pin helpers
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>apply_group_pins(step)</Code> — returns a{" "}
              <Code>{`{ [team_id] = group_id }`}</Code> lookup for all pins
              that carry a <Code>group_id</Code>. Safe to call even when{" "}
              <Code>step.manual_pins</Code> is nil.
            </List.Item>
            <List.Item>
              <Code>apply_slot_pins(group, step)</Code> — emits{" "}
              <Code>advance(team, game)</Code> for every pin whose{" "}
              <Code>game_id</Code> belongs to <Code>group</Code>. Returns a
              set of placed team ids, which the placement helpers consume to
              skip already-placed teams.
            </List.Item>
            <List.Item>
              <Code>add_seeding_rule_using(repartition, placement)</Code> —
              emits a <Code>STEP_SEEDING</Code> rule that chains the two
              helpers above with the chosen repartition and placement. The
              only seeding helper you typically need inside a preset script.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Withdrawal rule factories
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>add_bracket_withdrawal_rule()</Code> — for single /
              double / triple elimination. Pre-start: full reseed. Post-start:
              advance opponent as winner and forfeit remaining matches.
            </List.Item>
            <List.Item>
              <Code>add_scheduled_withdrawal_rule()</Code> — for round-robin,
              Swiss, league-home-away. Pre-start: reseed. Post-start: forfeit
              remaining games and bye-pair the team in future rounds.
            </List.Item>
            <List.Item>
              <Code>add_full_roster_withdrawal_rule()</Code> — for
              battle-royale, points-race, cumulative-rounds-series,
              cumulative-points-total. Pre-start: reseed. Post-start: release
              the team&apos;s slot from all WAITING games.
            </List.Item>
            <List.Item>
              <Code>forfeit_remaining_games(team)</Code>,{" "}
              <Code>advance_opponent_as_winner(game, team)</Code>,{" "}
              <Code>release_slot(team, game)</Code>,{" "}
              <Code>mark_withdrawn_for_future_rounds(team)</Code> — low-level
              primitives used by the factories above. Available to custom
              preset authors who need a bespoke withdrawal policy.
            </List.Item>
          </List>

          <Alert
            icon={<IconInfoCircle />}
            color="cyan"
            variant="light"
            mt="md"
          >
            <Text size="sm">
              <strong>Preset pinning metadata.</strong> When authoring a new
              preset, declare its pinning support via the{" "}
              <Code>metadata</Code> table:{" "}
              <Code>supportsPinning</Code>, <Code>supportsGroupPinning</Code>,{" "}
              and <Code>supportsSlotPinning</Code>. The console reads these to
              decide whether to surface the Group-pin / Slot-pin tabs in the
              seeding panel. Presets that set <Code>supportsPinning</Code> to
              false have their <Code>manual_pins</Code> rejected at validation
              time.
            </Text>
          </Alert>
        </div>

        {/* ===================== METADATA & CUSTOM FIELDS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconVariable size={18} />}
            title="Metadata & Custom Fields"
            id="metadata-custom-fields"
          />
          <Text mb="md">
            Lua scripts can attach arbitrary key/value data to a tournament
            entity and read it back from any later script execution. Two
            parallel surfaces are available:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>
                <Code>metadata</Code> / <Code>set_metadata</Code>
              </strong>{" "}
              — free-form key/value storage. No prior declaration required.
              Reach for this when you need a private scratchpad for an
              advancement rule (per-team counters, transient seed labels, flags
              consumed by a later rule firing).
            </List.Item>
            <List.Item>
              <strong>
                <Code>custom_field</Code> / <Code>set_custom_field</Code>
              </strong>{" "}
              — typed, organization-defined fields. Each key must be declared
              up-front as a <Code>CustomFieldDefinition</Code> (see below).
              Reach for this when the value is part of the public data model
              and should be visible / editable from the console.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Supported entity types
          </Title>
          <Text mb="xs" size="sm">
            Both pairs accept the same set of entities as their first argument.
            Any other value (a plain table, a <Code>match</Code> projection, an
            opaque id string) is rejected at validation time and silently
            no-ops at runtime.
          </Text>
          <List size="sm" spacing={4} mb="md">
            <List.Item>
              <Code>TournamentTeam</Code> — passed as the <Code>team</Code>{" "}
              global, or via <Code>by_id(team_id)</Code>.
            </List.Item>
            <List.Item>
              <Code>TournamentStep</Code> — passed as the <Code>step</Code>{" "}
              global.
            </List.Item>
            <List.Item>
              <Code>TournamentStepGroup</Code> — passed as the{" "}
              <Code>group</Code> global, or via <Code>step.groups[i]</Code>.
            </List.Item>
            <List.Item>
              <Code>TournamentStepGroupRound</Code> — passed as the{" "}
              <Code>round</Code> global, or via <Code>group.rounds[i]</Code>.
            </List.Item>
            <List.Item>
              <Code>TournamentStepGroupRoundGame</Code> — passed as the{" "}
              <Code>game</Code> global, or via <Code>round.games[i]</Code>.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Visibility parameter{" "}
            <Badge color="teal" variant="light" size="sm" ml="xs">
              New
            </Badge>
          </Title>
          <Text size="sm" mb="sm">
            <Code>set_metadata</Code> accepts an optional 4th argument that
            controls whether the stored entry is publicly readable or
            organization-private:
          </Text>
          <LuaBlock
            code={`-- Write a PUBLIC entry (visible to all callers, including anonymous)
set_metadata(team, "public_score", tostring(score), "PUBLIC")

-- Write a PRIVATE entry (default when 4th arg is omitted or nil)
set_metadata(team, "internal_note", "seed:3")          -- no 4th arg
set_metadata(team, "internal_note", "seed:3", "PRIVATE") -- explicit

-- Invalid value → Lua error (rule aborts; no partial writes)
-- set_metadata(team, "k", "v", "public")  -- lowercase not accepted`}
          />
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light" mt="sm" mb="md">
            Valid values are <Code>"PUBLIC"</Code> and{" "}
            <Code>"PRIVATE"</Code> (uppercase, string). Omitting the
            argument or passing <Code>nil</Code> defaults to{" "}
            <Code>PRIVATE</Code>. Any other value is rejected with a Lua
            error and the rule execution is aborted — no effects are
            persisted.
          </Alert>

          <Title order={4} mb="xs">
            Read / write contract
          </Title>
          <List size="sm" spacing={4} mb="md">
            <List.Item>
              <strong>Persistence.</strong> Values written by{" "}
              <Code>set_metadata</Code> / <Code>set_custom_field</Code> survive
              across rule firings, dispatches, and sessions. Each rule
              dispatch reloads the persisted values fresh at the start, so a
              script always sees the latest committed state.
            </List.Item>
            <List.Item>
              <strong>Same-script write-through.</strong> Within a single
              script execution, a <Code>set_metadata(team, "x", "1")</Code>{" "}
              followed by <Code>metadata(team, "x")</Code> returns{" "}
              <Code>"1"</Code> — writes are visible to subsequent reads in the
              same script without waiting for the dispatch to commit. The same
              applies to <Code>custom_field</Code>.
            </List.Item>
            <List.Item>
              <strong>Missing keys.</strong> Reading a key that was never
              written returns Lua <Code>nil</Code> — so the idiomatic{" "}
              <Code>metadata(t, &quot;foo&quot;) or default_value</Code> works
              cleanly.
            </List.Item>
            <List.Item>
              <strong>Strings only.</strong> The first three parameters of{" "}
              <Code>set_metadata</Code> must be strings (entity, key, value);
              the optional 4th is also a string. Reads always return a string
              (or <Code>nil</Code>). Authors who need numbers should{" "}
              <Code>tostring(value)</Code> on write and{" "}
              <Code>tonumber(metadata(t, &quot;k&quot;))</Code> on read.
              Booleans and tables must be serialised by the script (e.g.{" "}
              <Code>tostring(true)</Code>, JSON-encoded strings).
            </List.Item>
            <List.Item>
              <strong>Tenant scoping.</strong> Values are scoped to the
              organization that owns the tournament. A foreign-tenant entity
              id cannot be reached — writes against it silently no-op and
              reads return <Code>nil</Code>. There is no cross-organization
              metadata leakage.
            </List.Item>
            <List.Item>
              <strong>Entity-type fast path.</strong> Every entity exposed to
              Lua (<Code>team</Code>, <Code>step</Code>, <Code>group</Code>,
              {" "}<Code>round</Code>, <Code>game</Code>,{" "}
              <Code>tournament</Code>) carries a <Code>type</Code>{" "}
              discriminator the runtime reads when applying a{" "}
              <Code>set_metadata</Code> / <Code>set_custom_field</Code>{" "}
              effect. This avoids a probing query that otherwise resolves the
              entity's type from the database. The call shape is unchanged;
              the only author-visible consequence is faster cascades. Passing
              the result of <Code>by_id(id)</Code> (which carries only{" "}
              <Code>id</Code>) falls back to the probing path.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Worked example: per-team counter
          </Title>
          <Text mb="xs" size="sm">
            Increment a counter on a team across rule dispatches. Within the
            same script the write is visible to a subsequent read, so you can
            both update and act on the new value in one rule firing.
          </Text>
          <LuaBlock
            code={`-- Persist a per-team counter across rule dispatches.
local current = tonumber(metadata(team, "wins")) or 0
set_metadata(team, "wins", tostring(current + 1))

-- Read back inside the same script: write-through cache.
local updated = tonumber(metadata(team, "wins"))  -- equals current + 1`}
          />

          <Title order={4} mt="md" mb="xs">
            Worked example: step-scoped flag
          </Title>
          <Text mb="xs" size="sm">
            Stamp a step-level marker once and consume it from a later rule
            firing on the same step. The value round-trips through{" "}
            <Code>metadata(step, ...)</Code> on subsequent dispatches.
          </Text>
          <LuaBlock
            code={`-- First dispatch: record that seeding has been applied to this step.
set_metadata(step, "seeding_applied", "true")

-- Later dispatch (same step): branch on the persisted flag.
if metadata(step, "seeding_applied") == "true" then
  -- skip the seeding pass
end`}
          />

          <Title order={4} mt="md" mb="xs">
            Custom field prerequisite
          </Title>
          <Alert
            icon={<IconExclamationCircle />}
            color="yellow"
            variant="light"
            mb="md"
            title="set_custom_field requires a pre-defined custom field"
          >
            <Text size="sm">
              <Code>set_custom_field(entity, key, value)</Code> only persists
              when a <Code>CustomFieldDefinition</Code> already exists for the
              triple{" "}
              <Code>(organization, entity type, key)</Code>. Create the
              definition from the console&apos;s <strong>Custom Fields</strong>{" "}
              settings page (under Organization settings) before the script
              runs — pick the matching object type (Tournament Team,
              Tournament Step, Step Group, Round, or Game) and declare the key.
            </Text>
            <Text size="sm" mt="xs">
              When a definition is missing, the write is{" "}
              <strong>silently dropped</strong> at runtime; the same situation
              is surfaced as a structured error during rule-set / preset
              validation, so authors can catch it before deploying.{" "}
              <Code>custom_field(entity, key)</Code> will keep returning{" "}
              <Code>nil</Code>. <Code>set_metadata</Code> has no such
              prerequisite and works against any of the supported entity
              types.
            </Text>
          </Alert>
          <LuaBlock
            code={`-- Requires a CustomFieldDefinition with key="seed" on TournamentTeam
-- defined in the organization's Custom Fields settings before this runs.
set_custom_field(team, "seed", tostring(team.rank))
local seed = tonumber(custom_field(team, "seed"))`}
          />
        </div>

        {/* ===================== PRESET RE-APPLY SEMANTICS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTemplate size={18} />}
            title="Preset Re-Apply Semantics"
            id="preset-reapply"
          />
          <Text mb="md">
            Applying a preset to a step rule set is a{" "}
            <strong>replace, not a merge</strong>. In the same transaction,
            the engine wipes every existing artefact on the target step
            before persisting the effects emitted by the preset script:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              All <strong>advancement rules</strong> (including their
              actions).
            </List.Item>
            <List.Item>
              All <strong>cross-step rules</strong>.
            </List.Item>
            <List.Item>
              The <strong>structure template</strong> and every{" "}
              <Code>GroupTemplate</Code> / <Code>RoundTemplate</Code> /{" "}
              <Code>GameTemplate</Code> under it.
            </List.Item>
            <List.Item>
              The <strong>scoring rule set</strong> and every{" "}
              <Code>Tiebreaker</Code> it owns.
            </List.Item>
          </List>
          <Text mb="md">
            Re-applying the same preset, or switching from one preset to
            another, therefore no longer accumulates rules. Every apply
            produces the same rule set shape as a fresh apply would. This
            matches the expectation that &ldquo;apply&rdquo; means
            &ldquo;replace&rdquo;.
          </Text>
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            <Text size="sm">
              The wipe-and-replace runs in a rollback-safe transaction: if
              validation or script execution fails, the pre-existing rule set
              is preserved intact. Partial applies are not possible.
            </Text>
          </Alert>
        </div>

        {/* ===================== GENERATION API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPlaylistAdd size={18} />}
            title="Generation API"
            id="generation"
          />
          <Text mb="md">
            A <Code>GENERATION</Code> script materialises rounds and games for
            a single group that uses a custom generation script. Three globals
            are provided before execution and no return value is expected.
          </Text>

          <Title order={4} mb="xs">
            Injected globals
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>team_count</Code> — number of teams in the group (1 -
              10,000).
            </List.Item>
            <List.Item>
              <Code>best_of</Code> — best-of series length (1 - 99).
            </List.Item>
            <List.Item>
              <Code>group</Code> — <Code>{`{ id = "group-1" }`}</Code> by
              default, or the caller-supplied id when the pattern matches{" "}
              <Code>[A-Za-z0-9_-]{"{"}1,128{"}"}</Code>.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Available builders
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>create_round(group, config)</Code> — same semantics as in
              the Preset Builder API.
            </List.Item>
            <List.Item>
              <Code>create_game(round, config)</Code> — same.
            </List.Item>
            <List.Item>
              <Code>link(sourceGame, targetGame, outcome)</Code> — same.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Chain-layout seeding
          </Title>
          <Text size="sm" mb="md">
            When the generated structure is a <strong>chain layout</strong>{" "}
            &mdash; exactly 1 game per round, 2 team slots per game, and each
            game WIN-linked forward to the next round&apos;s game &mdash; the
            seeder auto-detects the topology and spreads one seed into each
            round&apos;s game rather than packing every seed into round 1.
            This is how the <Code>gauntlet</Code>, <Code>step-ladder</Code>,
            and <Code>king-of-the-hill</Code> built-in templates get their
            canonical bracket without any preset-author plumbing. Authoring a
            new chain-shaped preset requires no special seeding code: as long
            as the layout matches the shape above, the engine seeds it
            correctly.
          </Text>
        </div>

        {/* ===================== PARAMETERS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconClipboardList size={18} />}
            title="Parameters"
            id="parameters"
          />
          <Text mb="md">
            A preset script declares its user-facing parameters as{" "}
            <Code>PresetParameter</Code> records on the{" "}
            <Code>PresetScript</Code> object. Each parameter has a{" "}
            <Code>name</Code>, <Code>type</Code>,{" "}
            <Code>required</Code> flag, optional <Code>defaultValue</Code>{" "}
            (serialised as a string), and optional <Code>description</Code>.
            When the preset is applied, the user's values are injected as Lua
            globals before the script runs.
          </Text>

          <Title order={4} mb="xs">
            Supported primitive types (<Code>PresetParameterType</Code>)
          </Title>
          <Table striped highlightOnHover mb="md">
            <thead>
              <tr>
                <th>Type</th>
                <th>Lua representation</th>
                <th>Typical UI control</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>INT</Code>
                </td>
                <td>
                  <Code>number</Code> (integer)
                </td>
                <td>Number input with integer step</td>
              </tr>
              <tr>
                <td>
                  <Code>FLOAT</Code>
                </td>
                <td>
                  <Code>number</Code>
                </td>
                <td>Number input</td>
              </tr>
              <tr>
                <td>
                  <Code>STRING</Code>
                </td>
                <td>
                  <Code>string</Code>
                </td>
                <td>Text input</td>
              </tr>
              <tr>
                <td>
                  <Code>BOOLEAN</Code>
                </td>
                <td>
                  <Code>boolean</Code>
                </td>
                <td>Checkbox</td>
              </tr>
            </tbody>
          </Table>

          <LuaBlock code={PARAMETER_EXAMPLE} />
        </div>

        {/* ===================== SCRIPT LIMITS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconLock size={18} />}
            title="Script Limits & Restrictions"
            id="sandbox"
          />
          <Text mb="md">
            Every script runs inside a fresh, isolated environment. Scripts
            that exceed an execution limit are terminated; any side effects
            collected up to that point are discarded.
          </Text>

          <Title order={4} mb="xs">
            Execution limits
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Script size</strong> is bounded. Oversized scripts are
              rejected before execution.
            </List.Item>
            <List.Item>
              <strong>Execution time</strong> is bounded. Scripts that run too
              long abort with a <Code>TIMEOUT</Code> error.
            </List.Item>
            <List.Item>
              <strong>Instruction count</strong> is bounded. Scripts that run
              too many operations abort with an{" "}
              <Code>INSTRUCTION_LIMIT</Code> error.
            </List.Item>
            <List.Item>
              <strong>Memory usage</strong> is bounded per script.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Unavailable built-ins
          </Title>
          <Text mb="sm">
            The following built-ins are not available to user scripts. Calling
            any of them produces a <Code>BLOCKED_FUNCTION</Code> error:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            {UNAVAILABLE_GLOBALS.map((name, idx) => (
              <Badge
                key={name}
                color="red"
                variant="light"
                mr="xs"
                mb={idx < UNAVAILABLE_GLOBALS.length - 1 ? "xs" : 0}
              >
                <Code>{name}</Code>
              </Badge>
            ))}
          </Card>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>io</Code>, <Code>os</Code>, <Code>debug</Code> — no
              filesystem, clock, process, or debug access.
            </List.Item>
            <List.Item>
              <Code>package</Code>, <Code>require</Code>, <Code>loadfile</Code>
              , <Code>dofile</Code>, <Code>load</Code>,{" "}
              <Code>loadstring</Code> — no dynamic code loading.
            </List.Item>
            <List.Item>
              <Code>coroutine</Code> — coroutines are not supported.
            </List.Item>
            <List.Item>
              <Code>pcall</Code> / <Code>xpcall</Code> — error-catching is not
              available. Scripts cannot recover from runtime errors or limit
              breaches.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Other restrictions
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>No stdout:</strong> <Code>print</Code> is a no-op. Use
              return values or effects to surface data.
            </List.Item>
            <List.Item>
              <strong>No filesystem / network / process:</strong> scripts must
              be pure functions of their injected globals.
            </List.Item>
            <List.Item>
              <strong>No bytecode dumps:</strong> <Code>string.dump</Code> is
              unavailable.
            </List.Item>
            <List.Item>
              <strong>Isolated inputs:</strong> injected globals are cloned, so
              mutating them inside the script has no effect outside it.
            </List.Item>
          </List>
        </div>

        {/* ===================== ERROR / WARNING CODES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconExclamationCircle size={18} />}
            title="Error & Warning Codes"
            id="errors"
          />
          <Text mb="md">
            The Lua validator classifies every failure and concern with a
            machine-readable code. The full set is exposed on the GraphQL API
            via the <Code>ScriptErrorCode</Code> and{" "}
            <Code>ScriptWarningCode</Code> enums.
          </Text>
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>Code</th>
                <th>Severity</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {ERROR_WARNING_ROWS.map((row) => (
                <tr key={row.code}>
                  <td>
                    <Code>{row.code}</Code>
                  </td>
                  <td>
                    <Badge
                      color={row.kind === "error" ? "red" : "yellow"}
                      variant="light"
                    >
                      {row.kind}
                    </Badge>
                  </td>
                  <td>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* ===================== EXAMPLE: SINGLE ELIM ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="Example: Single-Elim Preset"
            id="example-single-elim"
          />
          <Text mb="md">
            The full content of the built-in single-elimination preset. It
            declares scoring, builds a bracket-shaped structure, links winners
            forward, and emits the canonical advancement, elimination, and
            promotion rules.
          </Text>
          <LuaBlock code={SINGLE_ELIM_EXAMPLE} />
          <Stack gap="xs" mt="md">
            <Text size="sm">
              <strong>Step 1 — Bracket sizing.</strong>{" "}
              <Code>next_pow2_and_log2</Code> is a built-in helper that rounds
              the team count up to the next power of 2 and returns the integer
              log2. Byes are implied for non-power-of-2 team counts.
            </Text>
            <Text size="sm">
              <strong>Step 2 — Scoring &amp; tiebreakers.</strong>{" "}
              <Code>set_scoring</Code> configures the step's scoring, and{" "}
              <Code>add_tiebreaker</Code> registers ordered tiebreakers.
            </Text>
            <Text size="sm">
              <strong>Step 3 — Structure.</strong> The nested loops create one
              bracket group, then per round a set of games, with{" "}
              <Code>link</Code> wiring each pair of games into the next
              round's game on the <Code>"WIN"</Code> outcome.
            </Text>
            <Text size="sm">
              <strong>Step 4 — Rules.</strong> The four helpers each emit a
              single <Code>add_rule</Code> effect for best-of resolution,
              winner advancement, loser elimination, and step-winner promotion.
            </Text>
          </Stack>
        </div>

        {/* ===================== EXAMPLE: SCORING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconMathFunction size={18} />}
            title="Example: Scoring Formula"
            id="example-scoring"
          />
          <Text mb="md">
            A <Code>FORMULA</Code> script computes a single team's score from
            its materialised match data. It must return a finite{" "}
            <Code>number</Code>.
          </Text>
          <LuaBlock code={SCORING_FORMULA_EXAMPLE} />
        </div>

        {/* ===================== EXAMPLE: ADVANCEMENT ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Example: Advancement Condition &amp; Action"
            id="example-advancement"
          />
          <Text mb="md">
            An advancement rule is a <Code>CONDITION</Code> script paired with
            one or more <Code>ACTION</Code> scripts. The condition decides
            whether the rule fires; the action emits the mutation effects.
          </Text>
          <Title order={4} mb="xs">
            Condition
          </Title>
          <LuaBlock code={ADVANCEMENT_CONDITION_EXAMPLE} />
          <Title order={4} mt="md" mb="xs">
            Action
          </Title>
          <LuaBlock code={ACTION_EXAMPLE} />
        </div>

        {/* ===================== BEST PRACTICES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconInfoCircle size={18} />}
            title="Best Practices"
            id="best-practices"
          />
          <List spacing="sm">
            <List.Item>
              <strong>Avoid unbounded loops.</strong> Scripts have bounded
              instruction and execution-time budgets; a runaway loop will hit
              one of them and abort. Bound loop counts by parameters derived
              from <Code>team_count</Code> or <Code>best_of</Code>.
            </List.Item>
            <List.Item>
              <strong>
                Do not use <Code>pcall</Code> / <Code>xpcall</Code>.
              </strong>{" "}
              Both are unavailable — runtime errors cannot be caught from
              inside a script.
            </List.Item>
            <List.Item>
              <strong>Keep scripts small.</strong> Large scripts slow down
              validation and approach the script-size limit. Prefer parameters
              and small named helpers over duplicated logic.
            </List.Item>
            <List.Item>
              <strong>Use parameters for tunable knobs.</strong> Anything a
              user might want to adjust (round count, best-of length, enabled
              flags) belongs in <Code>PresetParameter</Code> entries, not
              hard-coded literals.
            </List.Item>
            <List.Item>
              <strong>
                Prefer declarative preset-builder calls over raw loops.
              </strong>{" "}
              <Code>add_rule</Code>, <Code>add_tiebreaker</Code>, and{" "}
              <Code>set_scoring</Code> are the public contract — composing
              them from small, named helpers makes preset behaviour easy to
              audit.
            </List.Item>
            <List.Item>
              <strong>Fail fast with <Code>error(...)</Code>.</strong> When a
              parameter combination is unsupported, call{" "}
              <Code>error("...")</Code> — the host surfaces the message under
              <Code>RUNTIME</Code> error code with the offending line number.
            </List.Item>
            <List.Item>
              <strong>
                Never rely on filesystem, network, or wall-clock reads.
              </strong>{" "}
              <Code>io</Code>, <Code>os</Code>, and process globals are
              entirely removed — scripts must be pure functions of their inputs
              to stay reproducible.
            </List.Item>
          </List>
        </div>
      </Stack>
    </div>
  );
};
